import { SelectOption } from '$models'
import { roundNumber } from '$utils'
import { UiInvoiceLine } from '../../invoices/edit/helpers/uiInvoiceLine'

/**
 * Calculates the totals of invoice lines and formats them ready for display
 * @param lines The Invoice lines
 * @param lineItemCostsIncludeTax Whether or not items include tax or not (assumed false if undefined)
 * @param declaredInvoiceTotal The total that has been defined on the invoice (as opposed to the sum of line totals)
 * @returns object with subtotal, taxTotal, and total values
 */
export const calculateFooterTotals = (
  invoiceLines: UiInvoiceLine[],
  lineItemsIncludeTax: boolean,
  declaredInvoiceTotal: number | undefined,
  discountAmount: number = 0
): {
  subtotal: number
  taxTotal: number
  total: number
  roundingAmount: number | null
} => {
  const activeLines = invoiceLines.filter(
    (line) => line.deletedAt === undefined
  )

  const totals = activeLines.reduce(
    (acc, line) => {
      const unitCost = line.unitCost ?? 0
      const quantityReceived = line.quantityReceived ?? 0
      const taxAmount = calculateTaxOnItem(
        unitCost,
        quantityReceived,
        line.saleTaxRate ?? 0, // If saleTaxRate is undefined then we assume there is no tax
        lineItemsIncludeTax
      )

      const lineSubtotal = unitCost * quantityReceived

      return {
        subtotal:
          acc.subtotal +
          (lineItemsIncludeTax ? lineSubtotal - taxAmount : lineSubtotal),
        taxTotal: acc.taxTotal + taxAmount,
      }
    },
    { subtotal: 0, taxTotal: 0 }
  )
  const roundedTotals = {
    subtotal: roundNumber(totals.subtotal, 2),
    taxTotal: roundNumber(totals.taxTotal, 2),
    total: 0,
  }

  //Calculate at the end rather than in the reducer, as we can end up with a cent difference due to rounding
  roundedTotals.total = roundNumber(
    roundedTotals.subtotal + roundedTotals.taxTotal - discountAmount,
    2
  )

  const roundingAmount = declaredInvoiceTotal
    ? roundNumber(declaredInvoiceTotal - roundedTotals.total, 2)
    : 0

  // We round to 2dp here to get rid of cases where the number is for example '.0000000003'
  // And we make the assumption that most user will use 2dp in the Invoice Total input
  return {
    ...roundedTotals,
    //A rounding amount only exists if it is +- 10 cents, and not 0
    //(This threshhold is not currently configurable). See LOADED-5809.
    roundingAmount: isNonZeroWithinThreshold(roundingAmount, 0.1)
      ? roundingAmount
      : null,
  }
}

const isNonZeroWithinThreshold = (
  value: number,
  threshold: number
): boolean => {
  return (
    value >= -threshold &&
    value <= threshold &&
    Math.abs(value) > Number.EPSILON
  )
}

export const calculateTotalCost = (
  quantityReceived: number = 0,
  unitCost: number = 0
) => {
  return quantityReceived * unitCost
}

export const applyDiscount = (
  initialNumber: number | undefined = 0,
  discountPercent: number | undefined = 0
): number => initialNumber * (1 - discountPercent)

/**
 * Handles all relevant updates to fields after a Total Cost field change
 * @param formPrefix the name of the form's invoice line to update
 * @param updatedTotal the updated total
 */
export const handleTotalCostChange = (
  updateInvoiceLine: (
    lineId: string,
    updatedFields: Partial<UiInvoiceLine>
  ) => void,
  invoiceLine: UiInvoiceLine,
  updatedTotal: number,
  lineItemsIncludeTax: boolean
) => {
  let quantityReceived =
    invoiceLine?.quantityReceived ?? roundNumber(updatedTotal >= 0 ? 1 : -1, 3)

  // If quantityReceived's sign (negative or postive) does not match the updatedTotal's sign
  // update quantityRecieved to match the sign.
  // We want unitCost to always remain positive thus we change the quantityReceived.
  if (
    Math.sign(updatedTotal) != 0 &&
    Math.sign(quantityReceived!) !== Math.sign(updatedTotal)
  ) {
    quantityReceived = roundNumber(quantityReceived! * -1, 3)
  }

  // It's a bit counter-intuitive, but the Total field is ALWAYS
  // quantity recieved * unit cost (ignoring tax).
  // This was defined in LOADED-4495 and then modified in LOADED-5719
  const unitCost = updatedTotal / quantityReceived!
  const taxAmount = calculateTaxOnItem(
    unitCost,
    quantityReceived!,
    invoiceLine?.saleTaxRate ?? 0,
    lineItemsIncludeTax
  )

  updateInvoiceLine(invoiceLine?.id, {
    quantityReceived: quantityReceived,
    totalCost: updatedTotal,
    unitCost: roundNumber(unitCost, 2),
    taxAmount: roundNumber(taxAmount, 2),
  })
}

/**
 * Calculates the tax on each item doing correct calculations if GST Inclusive or Exclusive
 * @param unitCost The unitCost
 * @param quantityReceived The quantityReceived
 * @param taxRate Tax rate
 * @param gstInclusive Whether or not items include tax or not (assumed false if undefined)
 * @returns gstAmount
 */
export const calculateTaxOnItem = (
  unitCost: number,
  quantityReceived: number,
  taxRate: number,
  gstInclusive: boolean
): number => {
  const subtotal = unitCost * quantityReceived
  const gstAmount = gstInclusive
    ? (subtotal * taxRate) / (1 + taxRate)
    : subtotal * taxRate

  return gstAmount
}

/**
 * Filters a dictionary based on a provided filtering function
 * @param dictionary - The dictionary to filter
 * @param filterFn - The filtering function to apply. It receives a dictionary value and returns a boolean
 * @returns A new dictionary containing only the entries that pass the filtering function
 */
export function filterDictionary<T>(
  dictionary: Record<string, T>,
  filterFn: (value: T) => boolean
): Record<string, T> {
  return Object.keys(dictionary)
    .filter((key) => filterFn(dictionary[key]))
    .reduce((result, key) => {
      result[key] = dictionary[key]
      return result
    }, {} as Record<string, T>)
}

/**
 * Maps a dictionary to an array of SelectOptions
 * @param dictionary the dictionary to map
 * @param valueProperty the property to use as the 'value' of the SelectOption
 * @param labelProperty the property to use as the 'label' of the SelectOption
 * @param groupingProperty the property to use as the 'groupingValue' of the SelectOption
 * @returns All values of the dictionary as a array of SelectOptions
 */
export function dictionaryToSelectOption<T>(
  dictionary: Record<string, T>,
  valueProperty: string,
  labelProperty: string,
  groupingProperty?: string,
  keyProperty?: string,
  displayed?: (item: T) => boolean,
  disabled?: (item: T) => boolean
): SelectOption[] {
  return Object.values(dictionary).map(
    (value) =>
      ({
        key: keyProperty ? value[keyProperty] : undefined,
        label: value[labelProperty],
        value: value[valueProperty],
        groupingValue: groupingProperty ? value[groupingProperty] : undefined,
        display: displayed ? displayed(value) : true,
        disabled: disabled ? disabled(value) : false,
      } as SelectOption)
  )
}

/**
 * Maps a array to an array of SelectOptions
 * @param array the array to map
 * @param valueProperty the property to use as the 'value' of the SelectOption
 * @param labelProperty the property to use as the 'label' of the SelectOption
 * @param groupingProperty the property to use as the 'groupingValue' of the SelectOption
 * @returns All values of the array as a array of SelectOptions
 */
export function arrayToSelectOption<T>(
  array: T[],
  valueProperty: string,
  labelProperty: string,
  groupingProperty?: string
): SelectOption[] {
  return array.map(
    (value) =>
      ({
        label: value[labelProperty],
        value: value[valueProperty],
        groupingValue: groupingProperty ? value[groupingProperty] : undefined,
      } as SelectOption)
  )
}

/**
 * Formats a unit name by adding either " x " or a space before it, depending on its content.
 *
 * If the unit string contains a digit, " x " is added before the unit
 * to suggest a multiplier (e.g., "6Pack" becomes " x 6Pack").
 * Otherwise, a single space is added for consistent formatting.
 *
 * @param unit - The unit string to format (e.g., "kg", "6Pack").
 * @returns The formatted unit name with appropriate spacing or prefix.
 */
export const formatUnitName = (unit: string) => {
  const hasNumber = /\d/.test(unit)
  return `${hasNumber ? ' x ' : ' '}${unit}`
}

/**
 * Extracts a PO/CR number from text that may include prefixes like "PO#", "CR#", etc.
 * Handles cases where users copy-paste displayed order numbers (e.g., "PO#055399" -> "055399")
 * @param text The input text that may contain a PO number with prefix
 * @returns The extracted number (preserving leading zeros) or original text if no match
 */
export const extractPONumberOrSupplierName = (text: string): string => {
  const inputTrimmerRE = /(([CR|cr|PO|po])*\s*#?\s*)(\d+)$/

  const result = text.replace(inputTrimmerRE, '$3')

  return result
}
